<template>
	<div class="ts-container">
		<BackToSettings />
		<div class="ts-content">
			<div class="ts-title" v-text="'Diagnostics & Troubleshooting'" />

			<div class="ts-item">
				<Button @click="clearAppStorage" :loading="loading">
					Clear Application Storage
				</Button>
				<div class="item-description">
					If you are having technical difficulties, clearing your application
					storage may help. Prior to doing so, please download your log file
					above to preserve any diagnostic data. This page will refresh after
					the data has been cleared.
				</div>
			</div>

			<div class="ts-item">
				<div class="item-label">
					<ToggleSwitch v-model="enableRecording" />
					<div class="item-title">
						Send Advanced Diagnostics
					</div>
					<div class="item-detail" v-if="enableRecording && !formattedDate">
						Enabled indefinitely
					</div>
					<div
						class="item-detail"
						v-if="formattedDate"
						@click="showExtendModal = true"
					>
						Enabled until {{ formattedDate }}
					</div>
				</div>
				<div class="item-description">
					The Pronto team may ask you to enable this to help our engineers
					diagnose a problem. When enabled, information about your ongoing
					interactions with Pronto as well as advanced logging information will
					be recorded. We take precautions to exclude sensitive information such
					as message text and media in the sent data. This setting will
					automatically be disabled one week after you turn it on, or you can
					manually turn it off here anytime.
				</div>

				<div v-if="enableRecording">
					<Button link @click="downloadLogs">
						Download Application Log
					</Button>
					<div class="item-description">
						If instructed to do so by the Pronto team, download the application
						log file and send it to the team member for additional
						investigation.
					</div>
				</div>
			</div>
		</div>
		<StandardModal
			v-if="showExtendModal"
			open
			small
			title="Extend Recording Indefinitely?"
			confirmText="Yes, continue recording"
			@confirm="onExtendDate"
			@cancel="showExtendModal = false"
		>
			<div class="recording-date-modal">
				<p>
					Please only continue if instructed to do so by the Pronto team.
				</p>
				Currently your application sessions will be recorded until
				{{ formattedDate }}. At that time, recording will automatically stop. If
				you choose to continue, your sessions will continue to be recorded
				indefinitely.
			</div>
		</StandardModal>
	</div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import BackToSettings from './BackToSettings'
import { Button, ToggleSwitch, StandardModal } from '@/components/common'
import { downloadLogFile } from '@/helpers/log'
import { clearAppStorageAndRefresh } from '@/helpers/utils'
import {
	getEnabledDate,
	hasUserEnabled,
	saveEnableRecording,
	ENABLED_DURATION,
} from '@/helpers/session'
import moment from 'moment'

const isEnabled = hasUserEnabled()
const enableRecording = ref(isEnabled)
const loading = ref(false)
const enabledDate = ref(isEnabled ? getEnabledDate() : null)
const showExtendModal = ref(false)

const formattedDate = computed(() => {
	if (enabledDate.value && enabledDate.value instanceof Date)
		return moment(enabledDate.value)
			.add(ENABLED_DURATION, 'milliseconds')
			.format('lll')
	return null
})

watch(enableRecording, (enabled) => {
	saveEnableRecording(enabled)
	if (enabled) {
		enabledDate.value = new Date()
	} else {
		enabledDate.value = null
	}
})

function downloadLogs() {
	downloadLogFile()
}

async function clearAppStorage() {
	loading.value = true
	await clearAppStorageAndRefresh()
}

function onExtendDate() {
	saveEnableRecording(true, true)
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.ts-container {
	position: relative;
	width: 100%;
	padding: 80px;
	.ts-content {
		max-width: 600px;
		margin: 0 auto;
		.ts-title {
			width: 100%;
			text-align: center;
			font-size: 30px;
			margin-bottom: 32px;
		}
		.ts-item {
			@include flex-column(4px);
			align-items: flex-start;
			margin-bottom: 32px;
			.pronto-toggle {
				font-size: 24px;
				flex-shrink: 0;
			}
		}
		.item-label {
			@include flex(16px);
		}
		.item-description {
			font-size: 14px;
			color: var(--neutral-400);
			margin-top: 8px;
		}
		.item-detail {
			font-size: 14px;
			color: var(--neutral-400);
		}
	}
}
.recording-date-modal {
	padding: 32px;
	text-align: center;
	> p {
		@include fontFamily(800);
	}
}
</style>
